import { HttpClient } from '@adapters/HttpClient';
import { Printer } from '../models/PrinterModel';

export interface PrintersFilters {
  searchTerm?: string;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  status?: string;
  active?: string;
  page?: number;
  per_page?: number;
}

export interface PrintersPaginatedResponse {
  printers: Printer[];
  total: number;
  currentPage: number;
  totalPages: number;
  nextPage: number | null;
}

export class PrintersApiAdapter {
  constructor(private httpClient: HttpClient) {}

  // 🎯 Obtener impresoras paginadas
  async getPrintersPaginated(filters: PrintersFilters = {}): Promise<PrintersPaginatedResponse> {
    // 🎯 Construir URL directamente para evitar params[...]
    const urlParams = new URLSearchParams();
    urlParams.append('page', String(filters.page || 1));
    urlParams.append('per_page', String(filters.per_page || 15));
    urlParams.append('order_by', filters.sortBy || 'id');
    urlParams.append('order_direction', filters.sortDirection || 'desc');

    if (filters.searchTerm) {
      urlParams.append('search', filters.searchTerm);
    }
    if (filters.status) {
      urlParams.append('status', filters.status);
    }
    if (filters.active !== undefined) {
      urlParams.append('active', String(filters.active));
    }

    const response = await this.httpClient.get(`/printers?${urlParams.toString()}`);

    // 🎯 La API puede devolver { data: [...], meta: {...}, links: {...} } o directamente un array
    const responseData = (response as any).data;

    // 🎯 Verificar si responseData es un array directo o un objeto con estructura
    let printers, meta;

    if (Array.isArray(responseData)) {
      // 🎯 Si es un array directo, usar el array y valores por defecto
      printers = responseData;
      meta = { total: responseData.length, currentPage: 1, lastPage: 1 };
    } else {
      // 🎯 Si es un objeto, extraer las propiedades
      printers = responseData?.data || [];
      meta = responseData?.meta || {};
    }

    // 🎯 Calcular nextPage correctamente para useInfiniteQuery
    const currentPage = meta.currentPage || meta.current_page || 1;
    const totalPages = meta.lastPage || meta.last_page || 1;
    const nextPage = currentPage < totalPages ? currentPage + 1 : undefined;

    const result = {
      printers: printers,
      total: meta.total || printers.length,
      currentPage: currentPage,
      totalPages: totalPages,
      nextPage: nextPage,
    };

    console.log('🔍 PrintersApiAdapter - getPrintersPaginated response:', {
      url: `/printers?${urlParams.toString()}`,
      printersCount: result.printers.length,
      total: result.total,
      responseData,
      isArray: Array.isArray(responseData),
    });

    return result;
  }

  // 🎯 Obtener todas las impresoras
  async getAll(): Promise<Printer[]> {
    const response = await this.httpClient.get('/printers');
    const responseData = (response as any).data;
    // 🎯 La API devuelve { data: [...], meta: {...}, links: {...} }
    return responseData?.data || [];
  }

  // 🎯 Activar impresora (crear nueva)
  async activatePrinter(code: string, name: string): Promise<any> {
    const response = await this.httpClient.post('/printers', {
      code,
      name,
    });
    return (response as any).data;
  }

  // 🎯 Desactivar impresora (eliminar)
  async deactivatePrinter(printerCode: string): Promise<any> {
    const response = await this.httpClient.delete(`/printers/${printerCode}`);
    return (response as any).data;
  }

  // 🎯 Alternar estado de impresora
  async togglePrinter(printer: Printer): Promise<any> {
    if (printer.active) {
      // Si está activa, desactivarla (eliminar)
      return await this.deactivatePrinter(printer.code || printer.id.toString());
    } else {
      // Si está inactiva, activarla (crear)
      return await this.activatePrinter(printer.code || printer.id.toString(), printer.name);
    }
  }

  // 🎯 Actualizar impresora usando printerCode en la ruta
  async updatePrinter(printerCode: string, name: string): Promise<any> {
    const response = await this.httpClient.put(`/printers/${printerCode}`, { name });
    return (response as any).data;
  }

  // 🎯 Obtener configuración de impresoras
  async getSettings(): Promise<any> {
    const response = await this.httpClient.get('/printers/settings');
    return (response as any).data;
  }

  // 🎯 Actualizar configuración de impresoras
  async updateSettings(settings: any): Promise<any> {
    const response = await this.httpClient.post('/printers/settings', settings);
    return (response as any).data;
  }

  // 🎯 Obtener impresoras activas (método legacy)
  async getActivePrinters(): Promise<Printer[]> {
    const response = await this.httpClient.get('/printers?only_active=true');
    const responseData = (response as any).data;

    // 🎯 La API devuelve directamente un array o { data: [...], meta: {...}, links: {...} }
    if (Array.isArray(responseData)) {
      return responseData;
    } else {
      return responseData?.data || [];
    }
  }
}
